import { useEffect, useState } from 'react'

export type UseLoadMoreSentinelOptions = {
    /** More pages to fetch. */
    hasMore: boolean
    /** A fetch is in flight; nothing more is asked for until it settles. */
    loading: boolean
    onLoadMore: () => void
    /** How far before the sentinel scrolls into view to start loading. */
    rootMargin?: string
}

/**
 * Infinite scroll: put the returned ref on an element after the list. Whether it's in view is kept as state, so a page
 * that lands while the end is still on screen loads the next one straight away. A one-shot "it became visible" trigger
 * misses that case (the sentinel never leaves view, so it never fires again) and leaves the list stuck until you
 * scroll away and back.
 */
export function useLoadMoreSentinel({ hasMore, loading, onLoadMore, rootMargin = '200px' }: UseLoadMoreSentinelOptions) {
    const [sentinel, setSentinel] = useState<HTMLElement | null>(null)
    const [inView, setInView] = useState(false)

    useEffect(() => {
        if (!sentinel) return
        const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin })
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [sentinel, rootMargin])

    useEffect(() => {
        if (inView && hasMore && !loading) onLoadMore()
    }, [inView, hasMore, loading, onLoadMore])

    return setSentinel
}
